import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import {
  findAgent,
  scanAgentApi,
  type ScanAgentApi,
  type ScanAgentCapabilities,
  type ScanAgentDevice,
  type KeptScan,
  type ScanAgentUnavailableReason,
  type ScanOutcomeCode,
  type ScannedPage,
} from './scan-agent';
import { collectPages, trackOutcome, type ScanOutcome } from './scan-collect';
import { holdPage, releasePages, type HeldPage } from './scan-pages';
import { nextHeldIndex } from './scan-kept';
import { currentScanSession } from './scan-session';
import { useScanSpools, type ScanSpoolActions } from './useScanSpools';
import { scanSummary } from './scan-outcomes';
import { reportScanProblem } from './scan-problems';
import { trackProduct } from './product-tracker';
import { useScannerWindow, type ScannerWindowActions } from './useScannerWindow';
import {
  defaultScanOptions,
  fitOptions,
  rememberChoice,
  rememberDevice,
  rememberedDevice,
  rememberedOptions,
  type ScanOptions,
} from './scan-options';
import { scanAgentProblem } from './scan-agent-problem';

export type { ScanOptions };

export interface ScanAgentState {
  phase: 'checking' | 'unavailable' | 'ready' | 'scanning';
  reason: ScanAgentUnavailableReason | null;
  devices: ScanAgentDevice[];
  device: string;
  capabilities: ScanAgentCapabilities | null;
  options: ScanOptions;
  /** The pages so far, held as Blobs with object-URL previews (scan-pages.ts), never as base64. */
  pages: HeldPage[];
  /** Pages the scan in progress (or the last one) handed over. */
  scanned: number;
  /** Stop was asked; the sheets already inside the scanner still come. */
  stopping: boolean;
  problem: string | null;
  /** How the last scan ended, when it ended badly: the words come from it (scan-outcomes). */
  outcome: ScanOutcomeCode | null;
  /** A line after a scan that went well (blank sides left out), counted over every scan in the sheet. */
  summary: string | null;
  /** Blank sides left out since the sheet's pages were last cleared: every scan, not only the last. */
  blank: number;
  agentVersion: string;
  /** The scanner's own window drives the scan (a TWAIN scanner, chosen under More). */
  driverWindow: boolean;
  /** The scanner's settings window is open on this computer. */
  configuring: boolean;
  /** Scans Paige Scan kept on disk that no tab added (useScanSpools), and the one being brought back. */
  kept: KeptScan[];
  restoring: string | null;
}

export interface ScanAgentActions extends ScannerWindowActions {
  retry: () => void;
  chooseDevice: (id: string) => void;
  setOptions: (change: Partial<ScanOptions>) => void;
  scan: () => Promise<void>;
  cancel: () => Promise<void>;
  removePage: (index: number) => void;
  clearPages: () => void;
  report: (problem: string) => void;
  addKept: ScanSpoolActions['addKept'];
  discardKept: ScanSpoolActions['discardKept'];
}

type Update = (change: Partial<ScanAgentState>) => void;
type SetState = Dispatch<SetStateAction<ScanAgentState>>;
type ApiRef = RefObject<ScanAgentApi | null>;

/** How often a sheet waiting for Paige Scan to be installed or started looks again on its own. */
export const RECHECK_MS = 5000;
const initial: ScanAgentState = {
  phase: 'checking',
  reason: null,
  devices: [],
  device: '',
  capabilities: null,
  options: defaultScanOptions,
  pages: [],
  scanned: 0,
  stopping: false,
  problem: null,
  outcome: null,
  summary: null,
  blank: 0,
  agentVersion: '',
  driverWindow: false,
  configuring: false,
  kept: [],
  restoring: null,
};

/**
 * The scan sheet's state: finds Paige Scan on this computer (and keeps looking while it is not
 * installed or not running, so installing it is all a person has to do), lists its scanners, runs a
 * scan and collects the pages as the feeder hands them over. The agent never uploads; the sheet
 * stages the pages through the session when the person adds them to the job. A scan that ends badly
 * keeps its pages, says what to do, and is reported to Paige.
 */
export function useScanAgent(): [ScanAgentState, ScanAgentActions] {
  const [held, setState] = useState<ScanAgentState>(initial);
  const state = useFitted(held);
  const mounted = useRef(true);
  const update = useCallback<Update>((change) => {
    if (mounted.current) setState((prev) => ({ ...prev, ...change }));
  }, []);
  const api = useRef<ScanAgentApi | null>(null);
  const spools = useScanSpools(api, setState);
  const { discover, chooseDevice } = useDiscovery(update, api, spools.offer);
  const { scan, cancel } = useScanRun(api, state, update, setState, spools.track);
  const edits = usePageEdits(state.pages, setState, spools.releaseAll);
  const scannerWindow = useScannerWindow(api, state, update);
  useRecheck(state, discover);
  useEffect(() => {
    mounted.current = true;
    void discover();
    return () => {
      mounted.current = false;
    };
  }, [discover]);
  const report = useCallback((problem: string) => update({ problem, outcome: null }), [update]);
  return [
    state,
    {
      retry: () => void discover(),
      chooseDevice,
      addKept: spools.addKept,
      discardKept: spools.discardKept,
      scan,
      cancel,
      report,
      ...edits,
      ...scannerWindow,
    },
  ];
}

/** The options as the chosen scanner can take them (no feeder: the flatbed); the person's choice stays held. */
function useFitted(held: ScanAgentState): ScanAgentState {
  return useMemo(() => {
    const options = fitOptions(held.options, held.capabilities);
    return options === held.options ? held : { ...held, options };
  }, [held]);
}

/** Finding the agent and its scanners, then what it kept on disk; the chosen scanner's capabilities. Sets the agent's API. */
function useDiscovery(
  update: Update,
  apiRef: ApiRef,
  onFound: (api: ScanAgentApi) => Promise<void>,
) {
  const discover = useCallback(async () => {
    update({ phase: 'checking', reason: null, problem: null, outcome: null });
    const found = await findAgent();
    if (!found.agent) {
      trackProduct('scan.agent_missing', { reason: found.reason });
      return update({ phase: 'unavailable', reason: found.reason });
    }
    trackProduct('scan.agent_found');
    const api = scanAgentApi(found.agent);
    apiRef.current = api;
    await listScanners(api, found.agent.version, update);
    await onFound(api);
  }, [update, apiRef, onFound]);
  const chooseDevice = useCallback(
    (id: string) => {
      rememberDevice(id);
      update({
        device: id,
        options: rememberedOptions(id),
        capabilities: null,
        driverWindow: false,
      });
      apiRef.current
        ?.capabilities(id)
        .then((capabilities) => update(withWindow(capabilities)))
        .catch((error: unknown) => update({ problem: scanAgentProblem(error) }));
    },
    [update, apiRef],
  );
  return { discover, chooseDevice };
}

/** The agent's scanners, the remembered one chosen, and its capabilities; no scanner is its own reason. */
async function listScanners(
  api: ScanAgentApi,
  agentVersion: string,
  update: Update,
): Promise<void> {
  try {
    const devices = await api.devices();
    if (devices.length === 0) {
      trackProduct('scan.agent_missing', { reason: 'no_scanners' });
      return update({ phase: 'unavailable', reason: 'no_scanners', agentVersion });
    }
    const remembered = rememberedDevice();
    const device = devices.find((d) => d.id === remembered)?.id ?? devices[0]!.id;
    update({ phase: 'ready', devices, device, options: rememberedOptions(device), agentVersion });
    update(withWindow(await api.capabilities(device)));
  } catch (error) {
    trackProduct('scan.agent_missing', { reason: 'no_scanners' });
    update({ phase: 'unavailable', reason: 'no_scanners', problem: scanAgentProblem(error) });
  }
}

/** A scanner's capabilities, and its own window as this computer remembers it for that scanner. */
function withWindow(capabilities: ScanAgentCapabilities): Partial<ScanAgentState> {
  return {
    capabilities,
    driverWindow: Boolean(capabilities.driverWindow && capabilities.useDriverWindow),
  };
}

/**
 * While the sheet says Paige Scan is not installed or not running, it looks again every few seconds
 * (quickly: a repeat look waits seconds, not the first contact's half minute), so the sheet turns
 * into the scanner list by itself once the installer has run.
 */
function useRecheck(state: ScanAgentState, discover: () => Promise<void>): void {
  const waiting =
    state.phase === 'unavailable' &&
    (state.reason === 'not_installed' || state.reason === 'agent_off');
  useEffect(() => {
    if (!waiting) return undefined;
    let stopped = false;
    const timer = setInterval(() => {
      void findAgent({ reached: true, permission: async () => 'unknown' }).then((found) => {
        if (!stopped && found.agent) void discover();
      });
    }, RECHECK_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [waiting, discover]);
}

/** A scan starting: the last one's words and count go. */
const STARTING: Partial<ScanAgentState> = {
  phase: 'scanning',
  problem: null,
  outcome: null,
  summary: null,
  scanned: 0,
  stopping: false,
};

/** One scan: begin, poll the pages in, end; and the way to stop it early. Owns the scan in progress. */
function useScanRun(
  apiRef: ApiRef,
  state: ScanAgentState,
  update: Update,
  setState: SetState,
  track: (scan: string) => void,
) {
  const scanId = useRef<string | null>(null);
  const scan = useCallback(async () => {
    const api = apiRef.current;
    if (!api || state.phase !== 'ready' || state.configuring) return;
    update(STARTING);
    trackProduct('scan.started');
    try {
      const id = await api.begin(beginRequest(state));
      scanId.current = id;
      track(id);
      const outcome = await collectPages(api, id, holdInto(setState));
      settle(outcome, state, setState);
    } catch (error) {
      trackProduct('scan.failed', { reason: 'agent_error' });
      update({ phase: 'ready', stopping: false, problem: scanAgentProblem(error) });
    } finally {
      scanId.current = null;
    }
  }, [apiRef, state, update, setState, track]);
  const cancel = useCallback(async () => {
    const api = apiRef.current;
    if (!api || !scanId.current) return;
    update({ stopping: true });
    await api.cancel(scanId.current).catch(() => undefined);
  }, [apiRef, update]);
  return { scan, cancel };
}

/** Each page from the feeder, held with an index of the sheet's own: every scan numbers its pages from 1. */
function holdInto(setState: SetState): (page: ScannedPage) => void {
  return (page) => {
    const held = holdPage(page);
    setState((prev) => ({
      ...prev,
      pages: [...prev.pages, { ...held, index: nextHeldIndex(prev.pages) }],
      scanned: prev.scanned + 1,
    }));
  };
}

/** What Scan asks Paige Scan for: the scanner, the options, G4 for black and white, and the scanner's own window. */
function beginRequest(state: ScanAgentState): Parameters<ScanAgentApi['begin']>[0] {
  const jobId = currentScanSession()?.jobId;
  const request = {
    device: state.device,
    ...state.options,
    acceptTiff: true,
    ...(jobId ? { jobId } : {}),
  };
  return state.driverWindow ? { ...request, driverWindow: true } : request;
}

/**
 * The scan has ended: the words for it, the product event, and a report to Paige when it ended badly.
 * The blank sides add up over Scan more, so the line agrees with "Add N pages" beside it.
 */
function settle(outcome: ScanOutcome, state: ScanAgentState, setState: SetState): void {
  trackOutcome(outcome);
  setState((prev) => {
    const blank = prev.blank + outcome.blank;
    return {
      ...prev,
      phase: 'ready',
      stopping: false,
      problem: outcome.problem,
      outcome: outcome.code,
      blank,
      summary: outcome.failed ? null : scanSummary(prev.pages.length, blank),
    };
  });
  if (outcome.failed === 'scanner') {
    reportScanProblem({
      outcome: outcome.code ?? 'failed',
      scanner: state.devices.find((d) => d.id === state.device)?.name ?? 'scanner',
      layer: outcome.layer,
      pages: outcome.pages,
      agent: state.agentVersion,
      detail: outcome.detail,
    });
  }
}

/**
 * The options (each choice remembered on this computer) and the pages before they go to the job. A page that leaves the
 * sheet (removed, cleared after Add, or the sheet closed) gives its preview URL back.
 */
function usePageEdits(pages: HeldPage[], setState: SetState, releaseAll: () => void) {
  const held = useRef(pages);
  useEffect(() => {
    held.current = pages;
  }, [pages]);
  useEffect(() => () => releasePages(held.current), []);
  const setOptions = useCallback(
    (change: Partial<ScanOptions>) =>
      setState((prev) => {
        rememberChoice(prev.device, change);
        return { ...prev, options: { ...prev.options, ...change } };
      }),
    [setState],
  );
  const removePage = useCallback(
    (index: number) => {
      releasePages(held.current.filter((p) => p.index === index));
      setState((prev) => ({ ...prev, pages: prev.pages.filter((p) => p.index !== index) }));
    },
    [setState],
  );
  const clearPages = useCallback(() => {
    releasePages(held.current);
    releaseAll();
    setState((prev) => ({ ...prev, pages: [], summary: null, blank: 0 }));
  }, [setState, releaseAll]);
  return { setOptions, removePage, clearPages };
}
